import React from 'react';
import { Text, Flex, Box, Image } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { Button, Tooltip } from '@getro/rombo';
import { usePlaybooks } from 'pages/playbook/hooks/usePlaybooks';
import { ExternalLink } from 'lucide-react';
import { trackEvent } from 'helpers/analytics';
import ContactListSVG from '../../../assets/icon-contacts-list.svg';
import CompanyListSVG from '../../../assets/icon-companies-list.svg';

const Initial = ({ setStep, networkIsEcosystem = false }) => {
  const items = [
    {
      title: 'Contacts list',
      description: 'Stay organized with your contacts and share them when needed.',
      onClick: () => setStep('contacts:create'),
      icon: ContactListSVG,
      buttonText: 'Create list',
      isExperimental: false,
    },
    {
      title: 'Companies list',
      description: 'Discover warm intro paths to the companies on the list.',
      onClick: () => setStep('companies:create'),
      icon: CompanyListSVG,
      buttonText: 'Create list',
      isExperimental: true,
    },
  ];
  const playbooks = usePlaybooks();

  return (
    <Flex flexDirection="column" sx={{ gap: '80px' }}>
      <Box>
        <Text as="h4" fontWeight="medium" fontSize="14px" color="text.dark">
          What type of list do you want?
        </Text>
        <Flex sx={{ gap: '16px' }}>
          {items.map((item) => (
            <Flex
              key={item.title}
              mt="16px"
              p="16px"
              alignItems="center"
              sx={{
                gap: '16px',
                border: '1px solid',
                borderColor: 'border.subtle',
                borderRadius: 'default',
                flexDirection: 'column',
                '&:hover': {
                  bg: 'purple.100',
                  '#experimental-badge': {
                    bg: 'neutral.0',
                  },
                },
              }}
              onClick={item.onClick}
            >
              <Flex sx={{ justifyContent: 'space-between', gap: '4px', width: '100%' }}>
                <Image src={item.icon} width="24px" height="24px" />
                {item.isExperimental && (
                  <Tooltip
                    content="We're experimenting with this early-stage feature. If you notice anything funky, please let us know!"
                    placement="top-end"
                    sx={{
                      minWidth: '400px!important',
                    }}
                    trigger={
                      <Flex
                        id="experimental-badge"
                        bg="purple.100"
                        sx={{ borderRadius: '99px', alignItems: 'center', height: '24px', p: '4px 8px' }}
                      >
                        <Text fontSize="12px" color="purple.400" fontWeight="600">
                          Experimental
                        </Text>
                      </Flex>
                    }
                  />
                )}
              </Flex>

              <Box>
                <Text as="h5" fontWeight="500" fontSize="14px" color="text.dark">
                  {item.title}
                </Text>
                <Text mt="8px" fontSize="14px" color="text.dark">
                  {item.description}
                </Text>
              </Box>
              <Button size="small" variant="primary" width="100%" sx={{ justifyContent: 'center' }}>
                {item.buttonText}
              </Button>
            </Flex>
          ))}
        </Flex>
      </Box>
      {networkIsEcosystem && (
        <Flex flexDirection="row" alignItems="baseline" sx={{ gap: '16px', p: '16px', bg: 'neutral.20' }}>
          <Flex sx={{ alignItems: 'center', gap: '8px' }}>
            <Text fontSize="22px">💡</Text>
          </Flex>
          <Flex as="ul" sx={{ flexDirection: 'column', gap: '16px', listStyleType: 'disc', pl: '20px' }}>
            <Text color="text.dark" fontWeight={500}>
              Need some inspiration? Level up with plays from our playbook.
            </Text>
            {playbooks.map((playbook) => (
              <Box as="li" key={playbook.id} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                · {'   '}
                <Box
                  as="a"
                  href={playbook.link}
                  sx={{ color: 'text.main', textDecoration: 'underline', fontSize: '14px' }}
                  onClick={() => trackEvent('playbooks:see_playbook_click')}
                  target="_blank"
                >
                  {playbook.title}
                </Box>
                <Box as={ExternalLink} size={16} sx={{ color: 'neutral.400' }} />
              </Box>
            ))}
            <Button
              as="a"
              href="https://help.getro.com/support/solutions/folders/65000158491"
              target="_blank"
              size="small"
              width="200px"
              icon={ExternalLink}
              iconPosition="right"
              iconGap="8px"
              variant="secondary"
              sx={{ justifyContent: 'center', width: '185px' }}
              onClick={() => trackEvent('playbooks:see_all_playbooks_click')}
            >
              View full playbook
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

Initial.propTypes = {
  setStep: PropTypes.func.isRequired,
  networkIsEcosystem: PropTypes.bool,
};

Initial.defaultProps = {
  networkIsEcosystem: false,
};

export default Initial;
